import { RECAPTCHA_ERROR_MESSAGE } from "@am/multi_step_forms/recaptcha";
import { incompleteSteps } from "@am/multi_step_forms/validation";
import WizardReviewStep from "@am/multi_step_forms/WizardReviewStep";
import WizardSidebar from "@am/multi_step_forms/WizardSidebar";
import React from "react";
import { buildSubmitRequest } from "./api";
import FormStep from "./FormStep";
import SuccessScreen from "./SuccessScreen";
import { validateFascinatingFindsField } from "./validation";

const MAX_IMAGES = 5;

function fileIdentity(file) {
	return `${file.name}|${file.size}|${file.lastModified}`;
}

export default class FascinatingFindsWizard extends React.Component {
	constructor(props) {
		super(props);
		const { context } = props;
		this.state = {
			steps: context.steps || [],
			fields: context.fields || {},
			submitUrl: context.submit_url,
			maxImageMb: Number(context.max_image_size_mb) || 10,
			consent: context.consent || {},
			recaptcha: context.recaptcha || { enabled: false, sitekey: "", mode: "" },
			confirmationCta: context.confirmation_cta || {},
			currentStep: 0,
			view: "form",
			values: {},
			filesState: { photos: [] },
			errors: {},
			submitting: false,
			submitError: null,
		};
	}

	handleFieldChange(fieldId, value) {
		this.setState((prevState) => ({
			values: { ...prevState.values, [fieldId]: value },
		}));
	}

	handleFilesChange(fileList) {
		const maxImageMb = this.state.maxImageMb;
		const MAX_FILE_SIZE = maxImageMb * 1024 * 1024;
		const selected = Array.from(fileList || []);
		const currentPhotos = this.state.filesState.photos || [];
		const existingIds = new Set(
			currentPhotos.map((item) => fileIdentity(item.file)),
		);
		const remaining = MAX_IMAGES - currentPhotos.length;
		const additions = [];
		let rejectedForLimit = 0;
		selected.forEach((file) => {
			const id = fileIdentity(file);
			if (existingIds.has(id)) {
				window.alert(`You've already added ${file.name}.`);
			} else if (file.size > MAX_FILE_SIZE) {
				window.alert(
					`${file.name} is larger than ${maxImageMb} MB and wasn't added. Please choose a smaller image.`,
				);
			} else if (additions.length >= remaining) {
				rejectedForLimit += 1;
			} else {
				existingIds.add(id);
				additions.push({
					file,
					previewUrl: URL.createObjectURL(file),
					copyrightText: "",
					photographer: "",
				});
			}
		});
		if (rejectedForLimit > 0) {
			window.alert(
				`You can add up to ${MAX_IMAGES} images. ${rejectedForLimit} ${rejectedForLimit === 1 ? "image wasn't" : "images weren't"
				} added.`,
			);
		}
		this.setState((prevState) => ({
			filesState: {
				// Hard cap at MAX_IMAGES so the over-limit state can't occur;
				// validation still enforces the 3-5 range.
				photos: [...prevState.filesState.photos, ...additions],
			},
		}));
	}

	handleRemovePhoto(index) {
		this.setState((prevState) => {
			const target = prevState.filesState.photos[index];
			if (target && target.previewUrl) URL.revokeObjectURL(target.previewUrl);
			return {
				filesState: {
					photos: prevState.filesState.photos.filter((_, i) => i !== index),
				},
			};
		});
	}

	updatePhotoMetadata(index, key, value) {
		this.setState((prevState) => ({
			filesState: {
				...prevState.filesState,
				photos: (prevState.filesState.photos || []).map((item, i) =>
					i === index ? { ...item, [key]: value } : item,
				),
			},
		}));
	}

	renderPhotoReview(fieldId) {
		// null (not false) is the "render the default row" contract — false
		// suppresses the field entirely, which would hide every non-photo
		// answer on the review step.
		if (fieldId !== "photos") return null;
		const photos = this.state.filesState.photos || [];
		if (photos.length === 0) return false;
		const label = this.state.fields[fieldId]
			? this.state.fields[fieldId].label
			: "Photos";
		return (
			<div className="wizard-review-row" key={fieldId}>
				<dt>{label}</dt>
				<dd>
					<ul className="wizard-review-images">
						{photos.map((item, index) => (
							<li
								key={`${item.file.name}-${index}`}
								className="wizard-review-image-item"
							>
								{item.previewUrl && (
									<img
										src={item.previewUrl}
										alt=""
										className="wizard-review-thumbnail"
									/>
								)}
								<span className="wizard-review-image-info">
									{item.file.name}
									{item.copyrightText ? ` — ${item.copyrightText}` : ""}
									{item.photographer ? ` — ${item.photographer}` : ""}
								</span>
							</li>
						))}
					</ul>
				</dd>
			</div>
		);
	}

	scrollToTop() {
		if (typeof window !== "undefined" && window.scrollTo) {
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	}

	goNext() {
		this.scrollToTop();
		const { currentStep, steps } = this.state;
		if (currentStep === steps.length - 1) this.setState({ view: "review" });
		else this.setState({ currentStep: currentStep + 1 });
	}

	goBack() {
		this.scrollToTop();
		this.setState((prevState) => ({
			currentStep: Math.max(0, prevState.currentStep - 1),
		}));
	}

	handleSubmit(token, clientError) {
		if (clientError) {
			this.setState({ submitError: clientError, view: "review" });
			return;
		}
		const { values, filesState, submitUrl } = this.state;
		this.setState({ submitting: true, submitError: null });
		const csrfFn = window.get_csrf_token_value || (() => Promise.resolve(""));
		csrfFn()
			.then((csrfToken) =>
				buildSubmitRequest(values, filesState, csrfToken, submitUrl, token),
			)
			.then(() => {
				if (typeof window !== "undefined" && window.scrollTo) {
					window.scrollTo({ top: 0, behavior: "smooth" });
				}
				this.setState({ submitting: false, view: "confirmation" });
			})
			.catch((error) => {
				const responseErrors =
					error &&
					error.response &&
					error.response.body &&
					error.response.body.errors;
				const keys = responseErrors ? Object.keys(responseErrors) : [];
				const captchaOnly =
					keys.length > 0 && keys.every((key) => key === "captcha");
				this.setState({
					submitting: false,
					errors: captchaOnly ? {} : responseErrors || {},
					submitError: captchaOnly
						? RECAPTCHA_ERROR_MESSAGE
						: responseErrors
							? null
							: "Sorry, something went wrong. Please try again.",
					view: responseErrors && !captchaOnly ? "form" : "review",
				});
			});
	}

	render() {
		const {
			steps,
			fields,
			currentStep,
			view,
			values,
			filesState,
			consent,
			errors,
			submitting,
			submitError,
		} = this.state;
		if (view === "confirmation")
			return <SuccessScreen confirmationCta={this.state.confirmationCta} />;
		const incomplete = incompleteSteps(
			steps,
			fields,
			values,
			filesState,
			validateFascinatingFindsField,
		);
		if (view === "review") {
			return (
				<WizardReviewStep
					steps={steps}
					fields={fields}
					values={values}
					filesState={filesState}
					incompleteSteps={incomplete}
					onEditStep={(index) =>
						this.setState({ currentStep: index, view: "form" })
					}
					onBack={() => this.setState({ view: "form" })}
					onSubmit={this.handleSubmit.bind(this)}
					submitting={submitting}
					submitError={submitError}
					recaptcha={this.state.recaptcha}
					renderValue={this.renderPhotoReview.bind(this)}
				/>
			);
		}
		const current = steps[currentStep];
		return (
			<div className="wizard-layout">
				<WizardSidebar
					steps={steps}
					fields={fields}
					currentStep={currentStep}
					values={values}
					filesState={filesState}
					onStepClick={(index) => this.setState({ currentStep: index })}
					stepIsComplete={(step) => !incomplete.includes(step)}
				/>
				<div className="wizard-main">
					<FormStep
						step={current}
						fields={fields}
						values={values}
						filesState={filesState}
						consent={consent}
						errors={errors}
						onFieldChange={this.handleFieldChange.bind(this)}
						onFilesChange={this.handleFilesChange.bind(this)}
						onRemovePhoto={this.handleRemovePhoto.bind(this)}
						onUpdatePhotoMetadata={this.updatePhotoMetadata.bind(this)}
					/>
					<div className="wizard-nav">
						{currentStep > 0 && (
							<button type="button" onClick={this.goBack.bind(this)}>
								Back
							</button>
						)}
						<button
							type="button"
							onClick={this.goNext.bind(this)}
							disabled={incomplete.includes(current)}
						>
							{currentStep === steps.length - 1 ? "Review submission" : "Next"}
						</button>
					</div>
				</div>
			</div>
		);
	}
}
